import React from 'react';
import { Helmet } from "react-helmet-async"; // Cambiado a async
import { useTranslation } from "react-i18next";
import { businessLogic } from '../../lib/constants';
import { seoMetaFor } from '../../data/seoMeta';

// Solo para páginas sin descripción propia ni entrada en seoMeta.js (antes era una frase en
// inglés en una web en español, repetida en 16 páginas).
const DEFAULT_DESC = {
    es: "InvokeApp es la plataforma de atención al cliente, citas, CRM y seguimiento comercial para agencias y sus clientes, sin contratar un equipo técnico.",
    en: "InvokeApp is the customer service, appointments, CRM and sales follow-up platform for agencies and their clients, without hiring a technical team.",
};

const PageHelmet = ({ pageTitle, description, canonical, image, noindex = false, structuredData, children }) => {
    const baseUrl = businessLogic.domain; // dominio real

    // Idioma activo (es por defecto). Reactivo al cambio de idioma -> mantiene
    // <html lang> y og:locale sincronizados con el contenido (antes lang era fijo "en").
    const { i18n } = useTranslation();
    const lang = (i18n && i18n.language ? i18n.language : 'es')
        .toLowerCase()
        .startsWith('en') ? 'en' : 'es';
    const ogLocale = lang === 'en' ? 'en_US' : 'es_ES';
    const ogLocaleAlt = lang === 'en' ? 'es_ES' : 'en_US';

    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    const canonicalUrl = canonical || `${baseUrl}${currentPath}`;
    const ogImage = image || businessLogic.ogImage;
    // src/data/seoMeta.js manda sobre lo que pase la página (textos de buscador revisados en un
    // solo sitio). La marca se añade una vez, con su grafía: antes salía «| invokeapp» y, si el
    // título ya la llevaba, dos veces («Partners - InvokeApp | invokeapp»).
    const seo = seoMetaFor(currentPath, lang);
    const baseTitle = seo.title || pageTitle;
    const fullTitle = /invokeapp/i.test(baseTitle) ? baseTitle : `${baseTitle} | InvokeApp`;
    const metaDesc = seo.description || description || DEFAULT_DESC[lang];

    // JSON-LD sitewide: Organization + WebSite (entidad de marca en TODAS las
    // páginas; antes solo lo emitía Home). El schema por página se añade encima.
    const siteSchemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "InvokeApp",
        url: baseUrl,
        logo: `${baseUrl}/assets/img/logo/${businessLogic.logoBlack || "logo-black.png"}`,
        sameAs: [
          businessLogic.linkedin,
          businessLogic.x,
          businessLogic.instagram,
          businessLogic.facebook,
          businessLogic.tiktok,
          businessLogic.youtube,
        ].filter(Boolean),
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "InvokeApp",
        url: baseUrl,
      },
    ];
    const pageSchemas = Array.isArray(structuredData)
      ? structuredData
      : structuredData
      ? [structuredData]
      : [];
    const allSchemas = [...siteSchemas, ...pageSchemas].filter(Boolean);

    return (
        <Helmet htmlAttributes={{ lang }}>
            <meta charSet="utf-8" />
            <title>{fullTitle}</title>
            <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
            <meta name="description" content={metaDesc} />
            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />

            {/* Canonical + x-default. No hay routing por idioma (una sola URL sirve es/en),
                por eso NO declaramos hreflang en/es a la misma URL (era engañoso para Google). */}
            <link rel="canonical" href={canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

            {/* Open Graph (LinkedIn / WhatsApp / Facebook) */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDesc} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content="InvokeApp" />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:locale:alternate" content={ogLocaleAlt} />
            {ogImage ? <meta property="og:image" content={ogImage} /> : null}

            {/* Twitter / X Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@invokeapp_saas" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDesc} />
            {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

            {/* JSON-LD: Organization + WebSite (sitewide) + schema por página */}
            {allSchemas.map((schema, index) => (
              <script type="application/ld+json" key={`schema-${index}`}>
                {JSON.stringify(schema)}
              </script>
            ))}
            {children}
        </Helmet>
    );
};

export default PageHelmet;
